<script setup lang="ts">
import { useI18n } from '@/i18n';
import { useBillingStore } from '@/stores/billing';
import { Alert, Button, Divider, Heading, Input, LayoutGap, Loader, Prose, Radio, Textarea } from '@dabble/quillui';
import { computed, ref } from 'vue';
import PauseSubscriptionOptions from './PauseSubscriptionOptions.vue';

const YEARLY_DISCOUNT_CODE = 'promo_1Qkv1BGlCGS85NRPXWjUQHoO';
const MONTHLY_DISCOUNT_CODE = 'promo_1Qkv2KGlCGS85NRP90Pt0eYi';

const emit = defineEmits<{
  cancel: [];
  'subscription-cancelled': [];
}>();

const { t } = useI18n();
const billingStore = useBillingStore();

const selectedReason = ref<string>('');
const otherReasonDetail = ref<string>('');
const missingFeaturesDetail = ref<string>('');
const alternativeName = ref<string>('');
const technicalIssuesDetail = ref<string>('');
const feedback = ref<string>('');
const offerAccepted = ref(false);

const cancellationReasons = computed(() => [
  { value: 'too_expensive', label: t('exit_survey_reason_too_expensive') },
  { value: 'missing_features', label: t('exit_survey_reason_missing_features') },
  { value: 'found_alternative', label: t('exit_survey_reason_found_alternative') },
  { value: 'temporary_pause', label: t('exit_survey_reason_temporary_pause') },
  { value: 'not_using_enough', label: t('exit_survey_reason_not_using') },
  { value: 'technical_issues', label: t('exit_survey_reason_technical') },
  { value: 'other', label: t('exit_survey_reason_other') },
]);

const isYearlyPlan = computed(() => billingStore.primarySubscription?.plan?.interval === 'year');

const discountCode = computed(() => (isYearlyPlan.value ? YEARLY_DISCOUNT_CODE : MONTHLY_DISCOUNT_CODE));

const isReasonSelected = computed(() => {
  if (!selectedReason.value) return false;
  if (selectedReason.value === 'other') {
    return otherReasonDetail.value.trim().length > 0;
  }
  return true;
});

const handleSubmit = async () => {
  if (!isReasonSelected.value || billingStore.isLoading) return;

  const trimmed = (s: string) => s.trim() || undefined;

  const success = await billingStore.cancelSubscription({
    reason: selectedReason.value,
    feedback: trimmed(feedback.value),
    otherReason: selectedReason.value === 'other' ? trimmed(otherReasonDetail.value) : undefined,
    missingFeatures: selectedReason.value === 'missing_features' ? trimmed(missingFeaturesDetail.value) : undefined,
    alternativeName: selectedReason.value === 'found_alternative' ? trimmed(alternativeName.value) : undefined,
    technicalIssues: selectedReason.value === 'technical_issues' ? trimmed(technicalIssuesDetail.value) : undefined,
  });

  if (success) {
    setTimeout(() => {
      emit('subscription-cancelled');
    }, 500);
  }
};

async function handleAcceptOffer() {
  if (billingStore.isLoading || offerAccepted.value) return;
  const ok = await billingStore.applyCouponCode(discountCode.value);
  if (ok) {
    offerAccepted.value = true;
    setTimeout(() => {
      emit('subscription-cancelled');
    }, 500);
  }
}

function handlePausedInstead() {
  emit('subscription-cancelled');
}
</script>

<template>
  <form @submit.prevent="handleSubmit">
    <LayoutGap direction="column" size="medium">
      <Heading :level="2" align="center" no-margin>{{ t('exit_survey_title') }}</Heading>
      <Prose variant="secondary" align="center">{{ t('exit_survey_subheader') }}</Prose>

      <LayoutGap direction="row" size="medium" wrap class="cancel-survey__columns">
        <LayoutGap direction="column" size="small" class="cancel-survey__reasons">
          <Prose weight="semibold">{{ t('exit_survey_reason_label') }}</Prose>
          <LayoutGap direction="column" size="xsmall">
            <Radio
              v-for="reason in cancellationReasons"
              :key="reason.value"
              v-model="selectedReason"
              :value="reason.value"
              :label="reason.label"
              name="cancellationReason"
            />
          </LayoutGap>
        </LayoutGap>

        <LayoutGap direction="column" size="small" class="cancel-survey__detail">
          <Textarea
            v-if="selectedReason === 'other'"
            v-model="otherReasonDetail"
            :label="t('exit_survey_reason_other')"
            :placeholder="t('exit_survey_other_placeholder')"
            :rows="3"
            resize="vertical"
          />

          <Alert v-if="selectedReason === 'too_expensive' && !offerAccepted" variant="info" :no-icon="true">
            <LayoutGap direction="column" size="small">
              <Prose>{{ t('exit_survey_offer_pitch') }}</Prose>
              <LayoutGap direction="row" align="left" size="small">
                <Button type="button" variant="primary" :disabled="billingStore.isLoading" @click="handleAcceptOffer">
                  {{ t('exit_survey_offer_accept') }}
                </Button>
              </LayoutGap>
            </LayoutGap>
          </Alert>

          <Alert v-if="selectedReason === 'too_expensive' && offerAccepted" variant="success">
            {{ t('exit_survey_offer_applied') }}
          </Alert>

          <Textarea
            v-if="selectedReason === 'missing_features'"
            v-model="missingFeaturesDetail"
            :label="t('exit_survey_missing_features_label')"
            :placeholder="t('exit_survey_missing_features_placeholder')"
            :rows="3"
            resize="vertical"
          />

          <Input
            v-if="selectedReason === 'found_alternative'"
            v-model="alternativeName"
            type="text"
            :label="t('exit_survey_alternative_label')"
            :placeholder="t('exit_survey_alternative_placeholder')"
          />

          <Textarea
            v-if="selectedReason === 'technical_issues'"
            v-model="technicalIssuesDetail"
            :label="t('exit_survey_technical_label')"
            :placeholder="t('exit_survey_technical_placeholder')"
            :rows="3"
            resize="vertical"
          />

          <LayoutGap v-if="selectedReason === 'not_using_enough'" direction="column" size="small">
            <Alert v-if="isYearlyPlan" variant="info">
              {{ t('cancel_survey_pause_yearly_notice') }}
            </Alert>
            <template v-else>
              <Prose>{{ t('exit_survey_pause_pitch') }}</Prose>
              <PauseSubscriptionOptions
                from-cancel-survey
                @pause-subscription="handlePausedInstead"
                @back-to-billing="selectedReason = ''"
                @cancel="handleSubmit"
              />
            </template>
          </LayoutGap>

          <Textarea
            v-if="selectedReason && selectedReason !== 'not_using_enough' && selectedReason !== 'other'"
            v-model="feedback"
            :label="t('exit_survey_feedback_label')"
            :placeholder="t('exit_survey_feedback_placeholder')"
            :rows="4"
            resize="vertical"
          />
        </LayoutGap>
      </LayoutGap>

      <div role="alert" aria-live="assertive" aria-atomic="true">
        <Alert v-if="billingStore.error" variant="danger">
          {{ billingStore.error }}
        </Alert>
      </div>
    </LayoutGap>

    <div v-if="selectedReason !== 'not_using_enough'" class="cancel-survey__actions">
      <Divider />
      <LayoutGap direction="row" align="space-between" vertical-align="center" wrap size="small">
        <Button
          type="button"
          variant="secondary"
          finish="outlined"
          :disabled="billingStore.isLoading"
          @click="emit('cancel')"
        >
          {{ t('exit_survey_go_back') }}
        </Button>
        <LayoutGap v-if="billingStore.isLoading" direction="row" vertical-align="center" size="small">
          <Loader />
          <Prose variant="secondary">{{ t('exit_survey_processing') }}</Prose>
        </LayoutGap>
        <Button v-else type="submit" variant="danger" :disabled="!isReasonSelected">
          {{ t('exit_survey_cancel_subscription') }}
        </Button>
      </LayoutGap>
    </div>
  </form>
</template>

<style lang="scss" scoped>
.cancel-survey__columns {
  align-items: flex-start;
}

.cancel-survey__reasons {
  flex: 0 0 18rem;
  min-width: 14rem;

  :deep(label) {
    font-weight: 600;
  }
}

.cancel-survey__detail {
  flex: 1 1 20rem;
  min-width: 16rem;

  :deep(label) {
    font-weight: 600;
  }
}

.cancel-survey__offer {
  padding: 0.75rem;
  border-radius: 0.5rem;
  background-color: rgba(0, 0, 0, 0.03);
}

.cancel-survey__actions {
  margin-top: 1rem;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}
</style>
